import React, { useRef, useState } from "react";
import CookieSettings from "./CookieSettings";
import cookieService from "../services/cookieService";
import { useToast } from "../contexts/ToastContext";
import { useDetectedBrandLegal } from "../hooks/useDetectedBrandLegal";
import { LegalLink } from "./legal/LegalLink";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const Footer: React.FC = () => {
  // ADR-088 / spec 2026-07-06-per-brand-legal-content (AC B, H4): the ONLY
  // source for these URLs is GET /brands/detect — NOT domainConfig.ts, which
  // was hardcoded to kiedynabory.pl and wrong for HotelB2B.
  const { termsUrl, privacyUrl, isLoading: legalLoading } =
    useDetectedBrandLegal();
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    const existing = cookieService.getLocalConsent();
    return {
      essential: true,
      analytics: existing?.analytics ?? false,
      marketing: existing?.marketing ?? false,
    };
  });
  /**
   * Trigger button that opened the modal. Used to return focus on close
   * (WCAG 2.4.3 Focus Order).
   */
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { addToast } = useToast();

  const handleOpenSettings = () => {
    // Reload preferences from storage before opening
    const existing = cookieService.getLocalConsent();
    setPreferences({
      essential: true,
      analytics: existing?.analytics ?? false,
      marketing: existing?.marketing ?? false,
    });
    setShowCookieSettings(true);
  };

  const handleSettingsSave = async () => {
    try {
      await cookieService.acceptCustom(
        preferences.analytics,
        preferences.marketing,
      );
      setShowCookieSettings(false);
      // Return focus to the trigger (WCAG 2.4.3).
      triggerRef.current?.focus();
      // Inform the user that scripts (Clarity, GTM, Meta Pixel) need a reload
      // to apply the new consent. Existing trackers stay loaded until refresh.
      addToast({
        message: "Zmiany będą obowiązywać po odświeżeniu strony",
        type: "info",
      });
    } catch {
      addToast({
        message: "Nie udało się zapisać preferencji. Spróbuj ponownie.",
        type: "error",
      });
    }
  };

  const handleSettingsClose = () => {
    setShowCookieSettings(false);
    // Return focus to the trigger (WCAG 2.4.3).
    triggerRef.current?.focus();
  };

  return (
    <>
      <div className="mt-8 p-6">
        <div className="flex flex-row justify-between items-center gap-3 text-xs text-gray-500">
          <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
            <LegalLink
              url={privacyUrl}
              label="Polityka prywatności"
              isLoading={legalLoading}
              unavailableTestId="footer-privacy-link-unavailable"
              className="text-gray-600 hover:text-gray-800 hover:underline"
            />
            <LegalLink
              url={termsUrl}
              label="Regulamin"
              isLoading={legalLoading}
              unavailableTestId="footer-terms-link-unavailable"
              className="text-gray-600 hover:text-gray-800 hover:underline"
            />
            {/* Cookie settings button - minimum 44x44px touch target via padding */}
            <button
              ref={triggerRef}
              onClick={handleOpenSettings}
              className="text-gray-600 hover:text-gray-800 hover:underline text-xs py-3 px-2 min-h-[44px] min-w-[44px] text-left"
              aria-label="Otwórz ustawienia cookies"
            >
              Ustawienia cookies
            </button>
          </div>
          <div className="text-right whitespace-nowrap">
            <p>
              <a
                href="https://studio.kukla.tech"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 hover:underline"
              >
                studio.kukla.tech
              </a>
            </p>
          </div>
        </div>
      </div>

      {showCookieSettings && (
        <CookieSettings
          preferences={preferences}
          onPreferencesChange={setPreferences}
          onSave={handleSettingsSave}
          onClose={handleSettingsClose}
        />
      )}
    </>
  );
};

export default Footer;
